import type { ConversationView } from '@swchat/client-sdk';
import type { PersonalPrefs, Project } from '@swchat/protocol';
import { useCallback, useState, type CSSProperties } from 'react';
import { Avatar, Badge, Button, Icon, IconButton, Tabs } from '../design';
import {
  COLOR_HEX,
  filterByLabel,
  labelsOf,
  mutToggleGroupCollapsed,
  organize,
  prefOf,
  PrefsLimitError,
} from '../lib/conversation-prefs';
import { filterByProject, projectOptions, showProjectFilter } from '../lib/display';
import { useClient, usePrefs } from '../lib/hooks';
import { BRAND, shortTime } from './format';
import { LazyConversationMenu } from './lazy';

export type ConversationFilter = 'all' | 'unread' | 'project';

/** Bộ lọc nhanh của danh sách (W1.2): Chưa đọc = còn tin chưa đọc (bỏ nhóm đã rời); Dự án = nhóm gắn dự án. "Phòng ban" chờ W3. */
export function applyFilter<T extends Pick<ConversationView, 'unread_count' | 'left' | 'project_id'>>(list: readonly T[], filter: ConversationFilter): T[] {
  if (filter === 'unread') return list.filter((c) => c.unread_count > 0 && !c.left);
  if (filter === 'project') return list.filter((c) => !!c.project_id);
  return [...list];
}

/** Nhãn phím tắt mở bảng tìm kiếm theo hệ điều hành (⌘K trên Apple, Ctrl K nơi khác). */
const SEARCH_SHORTCUT = /Mac|iPhone|iPad/.test(typeof navigator === 'undefined' ? '' : navigator.platform) ? '⌘K' : 'Ctrl K';

/**
 * Cột danh sách hội thoại: tiêu đề, banner (thông báo, liên kết thiết bị), nút mở bảng tìm kiếm ⌘K (D1, W1.6), bộ lọc
 * nhanh, lọc theo dự án (E2) và theo nhãn cá nhân, danh sách chia phần Đã ghim / nhóm cá nhân / còn lại (tin cuối, giờ,
 * số chưa đọc A5). Nhãn, nhóm, ghim, highlight chỉ của tài khoản này (khối tùy chọn mã hóa phía client).
 */
export function ConversationList({
  conversations,
  selected,
  projects,
  filter,
  projectFilter,
  showNotify,
  linkRequest,
  embed,
  dark,
  onFilter,
  onProjectFilter,
  onSelect,
  onOpenSearch,
  onNewChat,
  onAllGroups,
  onEnableNotify,
  onLinkRequest,
  onDevices,
  onToggleTheme,
  onManage,
  onNotice,
}: {
  conversations: ConversationView[];
  selected?: string;
  projects: Project[];
  filter: ConversationFilter;
  projectFilter: string;
  showNotify: boolean;
  linkRequest: boolean;
  /**
   * Chế độ nhúng (không có thanh điều hướng): nút Thiết bị luôn nằm ở đầu danh sách. Web chính: nút Thiết bị và nút
   * sáng/tối ở đầu danh sách chỉ hiện khi thanh điều hướng bị ẩn (màn hẹp < 760px, CSS `.ws-rail-alt`).
   */
  embed: boolean;
  dark: boolean;
  onFilter: (f: ConversationFilter) => void;
  onProjectFilter: (id: string) => void;
  onSelect: (id: string) => void;
  /** Mở bảng tìm kiếm toàn cục (cũng mở bằng Ctrl+K / ⌘K ở Workspace). */
  onOpenSearch: () => void;
  onNewChat: () => void;
  /** Mở danh sách mọi nhóm mình tham gia (kể cả nhóm thiết bị này chưa đồng bộ). */
  onAllGroups: () => void;
  onEnableNotify: () => void;
  onLinkRequest: () => void;
  onDevices: () => void;
  onToggleTheme: () => void;
  /** Mở hộp thoại quản lý nhãn / nhóm. */
  onManage: (tab: 'labels' | 'groups') => void;
  onNotice: (message: string) => void;
}) {
  const client = useClient();
  const prefs = usePrefs();
  const [labelFilter, setLabelFilter] = useState('');
  const [menuFor, setMenuFor] = useState<string>();
  // Nhãn đã bị xóa mà bộ lọc còn trỏ tới → coi như không lọc.
  const activeLabel = prefs.labels.some((l) => l.id === labelFilter) ? labelFilter : '';
  const shown = filterByLabel(applyFilter(filterByProject(conversations, projectFilter), filter), prefs, activeLabel);
  const sections = organize(shown, prefs, { hideEmptyGroups: !!activeLabel || filter !== 'all' || !!projectFilter });
  const unreadConvs = conversations.filter((c) => c.unread_count > 0 && !c.left).length;
  const projectById = new Map(projects.map((p) => [p.id, p]));

  const change = useCallback(
    (mutate: (p: PersonalPrefs) => void) => {
      client.updatePrefs((p) => void mutate(p)).catch((e: unknown) => {
        onNotice(e instanceof PrefsLimitError ? e.message : 'Không lưu được thay đổi, hãy thử lại');
      });
    },
    [client, onNotice],
  );
  const closeMenu = useCallback(() => setMenuFor(undefined), []);
  /** Xóa hội thoại khỏi danh sách của mình (chat 1-1 hoặc nhóm đã rời) — không ảnh hưởng người khác. */
  const removeConversation = async (c: ConversationView) => {
    const text =
      c.kind === 'direct'
        ? `Xóa cuộc hội thoại với “${c.title}”?\n\nLịch sử sẽ bị xóa khỏi tài khoản của bạn trên mọi thiết bị. Người kia vẫn giữ tin nhắn của họ. Nếu họ nhắn lại, hội thoại sẽ hiện lại và chỉ có tin mới.`
        : `Xóa nhóm đã rời “${c.title}” khỏi danh sách?\n\nLịch sử nhóm sẽ bị xóa khỏi tài khoản của bạn trên mọi thiết bị, không thể khôi phục. Các thành viên khác không bị ảnh hưởng.`;
    if (!window.confirm(text)) return;
    try {
      await client.hideConversation(c.id);
      onNotice('Đã xóa hội thoại');
    } catch (e) {
      onNotice(e instanceof Error ? e.message : 'Không xóa được hội thoại');
    }
  };

  const renderItem = (c: ConversationView) => {
    const project = c.project_id ? projectById.get(c.project_id) : undefined;
    const pref = prefOf(prefs, c.id);
    const labels = labelsOf(prefs, c.id);
    const pinned = pref?.pinned !== undefined;
    return (
      <li key={c.id} className="ws-conv-wrap" onContextMenu={(e) => (e.preventDefault(), setMenuFor(c.id))}>
        <button
          type="button"
          className={['ws-conv', c.id === selected ? 'active' : '', c.left ? 'left' : '', pref?.highlight ? 'highlighted' : ''].filter(Boolean).join(' ')}
          style={pref?.highlight ? ({ '--ws-hl': COLOR_HEX[pref.highlight] } as CSSProperties) : undefined}
          aria-current={c.id === selected ? 'true' : undefined}
          data-testid="conv-item"
          data-conv-id={c.id}
          data-conv-title={c.title}
          data-pinned={pinned ? 'true' : undefined}
          data-highlight={pref?.highlight}
          onClick={() => onSelect(c.id)}
        >
          <Avatar name={c.title} seed={c.id} size="md" />
          <span className="ws-conv-main">
            <span className="ws-conv-line">
              <span className="ws-conv-title" data-testid="conv-title">
                {pinned && <Icon name="pin" size={12} />}
                {c.title}
                {c.warning && <span className="ws-warn-dot" title={c.warning} />}
                {c.left && <span className="ws-muted ws-small"> · đã rời</span>}
              </span>
              <time className="ws-conv-time" data-testid="conv-time">
                {c.last_message ? shortTime(c.last_activity_ts) : ''}
              </time>
            </span>
            <span className="ws-conv-line">
              <span className={`ws-conv-preview${c.unread_count ? ' unread' : ''}`} data-testid="conv-preview">
                {c.last_message
                  ? `${c.last_message.sender_user_id === client.userId ? 'Bạn: ' : c.kind === 'direct' ? '' : `${client.getUser(c.last_message.sender_user_id)?.display_name ?? '…'}: `}${c.last_message.text}`
                  : c.kind === 'direct'
                    ? 'Chat 1-1'
                    : `${c.members.length} người`}
              </span>
              {project && <Badge tone="neutral">#{project.code}</Badge>}
              {c.unread_count > 0 && !c.left && (
                <span className="ws-unread" data-testid="conv-unread">
                  {c.unread_count > 99 ? '99+' : c.unread_count}
                </span>
              )}
            </span>
            {labels.length > 0 && (
              <span className="ws-conv-labels" data-testid="conv-labels">
                {labels.map((l) => (
                  <span key={l.id} className="ws-chip" style={{ '--ws-chip': COLOR_HEX[l.color] } as CSSProperties} data-testid="conv-label">
                    {l.name}
                  </span>
                ))}
              </span>
            )}
          </span>
        </button>
        <IconButton
          label={`Tùy chọn hội thoại ${c.title}`}
          icon="more"
          className="ws-conv-more"
          aria-haspopup="menu"
          aria-expanded={menuFor === c.id}
          data-testid="conv-more"
          onClick={() => setMenuFor(menuFor === c.id ? undefined : c.id)}
        />
        {menuFor === c.id && (
          <LazyConversationMenu
            conversationId={c.id}
            prefs={prefs}
            onChange={change}
            onManageLabels={() => onManage('labels')}
            onManageGroups={() => onManage('groups')}
            onDelete={c.kind === 'direct' || c.left ? () => void removeConversation(c) : undefined}
            onClose={closeMenu}
          />
        )}
      </li>
    );
  };

  return (
    <aside className="ws-inbox" aria-label="Danh sách hội thoại">
      <header className="ws-inbox-head">
        <div className="ws-grow">
          <h1>{BRAND}</h1>
          <div className="ws-muted ws-small ws-ellipsis">
            {client.displayName} · <Icon name="lock" size={11} /> Mã hóa đầu cuối
          </div>
        </div>
        {!embed && (
          <IconButton
            label={dark ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'}
            icon={dark ? 'sun' : 'moon'}
            className="ws-rail-alt"
            data-testid="inbox-theme-toggle"
            onClick={onToggleTheme}
          />
        )}
        <IconButton
          label="Thiết bị"
          icon="device"
          className={embed ? undefined : 'ws-rail-alt'}
          data-testid="inbox-devices"
          onClick={onDevices}
        />
        <Button variant="ghost" size="sm" data-testid="all-groups" onClick={onAllGroups}>
          Tất cả nhóm
        </Button>
        <Button variant="primary" size="sm" icon="plus" data-testid="new-chat" onClick={onNewChat}>
          Chat
        </Button>
      </header>
      {/* Iframe khác origin (nhúng portal) không được xin quyền Notification → portal tự báo qua `unread`. */}
      {showNotify && (
        <button type="button" className="ws-banner ws-banner-info" data-testid="enable-notifications" onClick={onEnableNotify}>
          <Icon name="bell" size={16} />
          Bật thông báo khi có tin nhắn mới
        </button>
      )}
      {linkRequest && (
        <button type="button" className="ws-banner" onClick={onLinkRequest}>
          <Icon name="device" size={16} />
          Có thiết bị mới đang chờ liên kết — mở để phê duyệt
        </button>
      )}
      <div className="ws-inbox-tools">
        <button
          type="button"
          className="ws-search ws-search-trigger"
          data-testid="open-search"
          aria-haspopup="dialog"
          aria-keyshortcuts="Control+K Meta+K"
          onClick={onOpenSearch}
        >
          <Icon name="search" size={15} />
          <span>Tìm kiếm…</span>
          <kbd className="ws-kbd">{SEARCH_SHORTCUT}</kbd>
        </button>
        <Tabs
          label="Lọc hội thoại"
          variant="pill"
          value={filter}
          onChange={onFilter}
          items={[
            { id: 'all', label: 'Tất cả', testId: 'filter-all' },
            { id: 'unread', label: 'Chưa đọc', count: unreadConvs, testId: 'filter-unread' },
            { id: 'project', label: 'Dự án', testId: 'filter-project' },
          ]}
        />
        {showProjectFilter(projects, '') && (
          <select className="ws-input ws-select" data-testid="project-filter" value={projectFilter} onChange={(e) => onProjectFilter(e.target.value)} aria-label="Lọc theo dự án">
            {projectOptions(projects, 'Tất cả hội thoại').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        <div className="ws-row ws-label-filter">
          {prefs.labels.length > 0 && (
            <select className="ws-input ws-select ws-grow" data-testid="label-filter" value={activeLabel} onChange={(e) => setLabelFilter(e.target.value)} aria-label="Lọc theo nhãn">
              <option value="">Mọi nhãn</option>
              {prefs.labels.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          )}
          <Button size="sm" icon="settings2" data-testid="open-organize" onClick={() => onManage('labels')}>
            Nhãn &amp; nhóm
          </Button>
        </div>
      </div>
      <ul className="ws-conv-list" data-testid="conv-list">
        {sections.map((s) => {
          if (s.kind === 'none' && sections.length === 1) return s.items.map(renderItem);
          const collapsed = s.kind === 'group' && !!s.group?.collapsed;
          const unread = s.items.reduce((n, c) => n + (c.left ? 0 : c.unread_count), 0);
          const title = s.kind === 'pinned' ? 'Đã ghim' : s.kind === 'group' ? s.group!.name : 'Khác';
          return (
            <li key={s.key} className="ws-conv-section" data-testid="conv-section" data-section={s.key}>
              <div className="ws-section-head">
                {s.kind === 'group' ? (
                  <button
                    type="button"
                    className="ws-section-toggle"
                    aria-expanded={!collapsed}
                    data-testid="group-toggle"
                    onClick={() => change((p) => mutToggleGroupCollapsed(p, s.group!.id))}
                  >
                    <Icon name={collapsed ? 'chevron' : 'down'} size={13} />
                    <Icon name="folder" size={13} />
                    <span className="ws-ellipsis">{title}</span>
                  </button>
                ) : (
                  <span className="ws-section-toggle ws-section-static">
                    {s.kind === 'pinned' && <Icon name="pin" size={13} />}
                    <span>{title}</span>
                  </span>
                )}
                <span className="ws-muted ws-small">{s.items.length}</span>
                {collapsed && unread > 0 && <span className="ws-unread">{unread > 99 ? '99+' : unread}</span>}
              </div>
              {!collapsed && (
                <ul className="ws-conv-sublist">
                  {s.items.map(renderItem)}
                  {!s.items.length && <li className="ws-muted ws-small ws-section-empty">Chưa có hội thoại — dùng menu ⋯ của hội thoại để thêm vào nhóm.</li>}
                </ul>
              )}
            </li>
          );
        })}
        {!conversations.length && <li className="ws-muted ws-empty">Chưa có cuộc trò chuyện. Bấm “+ Chat” để bắt đầu.</li>}
        {conversations.length > 0 && !shown.length && (
          <li className="ws-muted ws-empty" data-testid="conv-empty">
            {activeLabel ? 'Chưa có hội thoại nào gắn nhãn này.' : projectFilter ? 'Dự án này chưa có nhóm nào.' : filter === 'unread' ? 'Không có hội thoại chưa đọc.' : 'Chưa có hội thoại gắn dự án.'}
          </li>
        )}
      </ul>
    </aside>
  );
}
